import type {ContextProvider, ContextProviderDataGeneric} from '@bitstillery/common/lib/context'
import {classes} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button, Icon} from '@bitstillery/common/components'
import {$s, store} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'

interface PanelContextAttrs {
    className?: string
    collection?: CollectionProxy | undefined
    /** A ContextProvider */
    context?: ContextProvider<ContextProviderDataGeneric>
    count?: number
    icon?: string
    keep_open?: boolean
    minimizable?: boolean
    /** Pin the panel open: no collapse/close control, and entity selection can't fold it away. */
    no_collapse?: boolean
    no_float?: boolean
    title?: (() => string) | string
}

export class PanelContext extends MithrilComponent<PanelContextAttrs> {
    override oninit(vnode: m.Vnode<PanelContextAttrs>) {
        if (vnode.attrs.context?.data.entity_artkey) {
            $s.panels.context.active = true
        } else {
            $s.panels.context.active = false
        }
    }

    override onupdate(vnode: m.Vnode<PanelContextAttrs>) {
        if (vnode.attrs.context?.data.entity_artkey) {
            $s.panels.context.active = true
        } else {
            $s.panels.context.active = false
        }
    }

    override onremove(vnode: m.Vnode<PanelContextAttrs>) {
        if (!vnode.attrs.keep_open) {
            $s.panels.context.collapsed = true
        }
        $s.panels.context.active = false
    }

    view(vnode: m.Vnode<PanelContextAttrs>): m.Children {
        const {collection, context} = vnode.attrs
        return (
            <div
                className={classes('c-panel-context', vnode.attrs.className, {
                    collapsed: !vnode.attrs.no_collapse && $s.panels.context.collapsed,
                    'float-panel': !vnode.attrs.no_float,
                    'float-panel--active': !vnode.attrs.no_float && $s.env.layout === 'mobile',
                    minimizable: vnode.attrs.minimizable,
                })}
                onkeydown={(e: KeyboardEvent) => {
                    // Only navigate context when ctrl is pressed.
                    if (!e.ctrlKey) return
                    if (!context || !vnode.attrs.collection) return
                    if (e.key === 'ArrowUp') {
                        context.select_previous(vnode.attrs.collection)
                    } else if (e.key === 'ArrowDown') {
                        context.select_next(vnode.attrs.collection)
                    }
                }}
                tabindex={10}
            >
                {/* A floating panel's header is a chrome bar, where an info toggle's own chrome fill vanishes. */}
                <header className={classes({'on-chrome': !vnode.attrs.no_float})}>
                    <div className='title'>
                        <div className='icon'>
                            <Icon name={vnode.attrs.icon ? vnode.attrs.icon : 'card_account_details_outline'} type='info' />
                        </div>
                        <span>
                            {(() => {
                                // An explicit title beats the context's entity title: a panel that names
                                // itself isn't showing that entity — the CMS assistant shares a context
                                // with the list behind it and would otherwise be titled per tab.
                                if (vnode.attrs.title) {
                                    const title =
                                        typeof vnode.attrs.title === 'function' ? vnode.attrs.title() : vnode.attrs.title
                                    if (title) {
                                        return title
                                    }
                                }
                                if (context) {
                                    if (context.data.title) {
                                        return context.data.title
                                    } else {
                                        const entity_type = context.data.entity_type

                                        if (entity_type) {
                                            return context.data.entities[entity_type]?.title
                                        }
                                    }
                                }
                                return 'Context Panel'
                            })()}
                        </span>
                    </div>
                    <div className='panel-actions'>
                        {context &&
                            collection &&
                            // (!) only available in edit-mode
                            context.data.entity_artkey && [
                                <Button
                                    icon='arrowUpCircle'
                                    onclick={() => context.select_previous(collection)}
                                    tip='Previous item in the collection (ctrl + ↑)'
                                    type='info'
                                    variant='toggle'
                                />,
                                <Button
                                    icon='arrowDownCircle'
                                    onclick={() => context.select_next(collection)}
                                    tip='Next item in the collection (ctrl + ↓)'
                                    type='info'
                                    variant='toggle'
                                />,
                            ]}

                        {!vnode.attrs.no_collapse && vnode.attrs.minimizable && (
                            <Button
                                className={classes('c-panel-minimize-toggle', {
                                    'is-collapsed': $s.panels.context.collapsed,
                                })}
                                icon={$s.panels.context.collapsed ? 'menuOpen' : 'chevronRight'}
                                tip={$s.panels.context.collapsed ? 'Expand panel' : 'Collapse panel'}
                                onclick={() => {
                                    $s.panels.context.collapsed = !$s.panels.context.collapsed
                                    void store.save()
                                }}
                                variant='toggle'
                            />
                        )}

                        {!vnode.attrs.no_collapse && !vnode.attrs.minimizable && (
                            <Button
                                icon='close'
                                tip='Close this panel'
                                onclick={() => {
                                    if (context) {
                                        context.select_none(vnode.attrs.collection)
                                    } else {
                                        $s.panels.context.collapsed = true
                                    }
                                }}
                                variant='toggle'
                            />
                        )}
                    </div>
                </header>
                {vnode.children}
            </div>
        )
    }
}
